import BigNumber from 'bignumber.js'
import { LotteryResponse, LotteryRound, LotteryRoundUserTickets } from 'state/types'

const API_LOTTERY = process.env.REACT_APP_API_LOTTERY
const API_REFERRAL = process.env.REACT_APP_REFERRAL_API_URL

/**
 * Remove the '1' and reverse the digits in a lottery number retrieved from the smart contract
 */
export const parseRetrievedNumber = (number: string): string => {
  const numberAsArray = number.split('')
  numberAsArray.splice(0, 1)
  numberAsArray.reverse()
  return numberAsArray.join('')
}

export const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

export const timeOptions: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
}

export const dateTimeOptions: Intl.DateTimeFormatOptions = {
  ...dateOptions,
  ...timeOptions,
}

export const getDrawnDate = (locale: string, endTime: string) => {
  const endTimeInMs = parseInt(endTime, 10) * 1000
  const endTimeAsDate = new Date(endTimeInMs)
  return endTimeAsDate.toLocaleDateString(locale, dateTimeOptions)
}

export const processLotteryResponse = (
  lotteryData: LotteryResponse & { userTickets?: LotteryRoundUserTickets }
): LotteryRound => {
  const {
    priceTicketInMjt: priceTicketInMjtAsString,
    discountDivisor: discountDivisorAsString,
    amountCollectedInMjt: amountCollectedInMjtAsString,
  } = lotteryData

  const discountDivisor = new BigNumber(discountDivisorAsString)
  const priceTicketInMjt = new BigNumber(priceTicketInMjtAsString)
  const amountCollectedInMjt = new BigNumber(amountCollectedInMjtAsString)

  return {
    isLoading: lotteryData.isLoading,
    lotteryId: lotteryData.lotteryId,
    userTickets: lotteryData.userTickets,
    status: lotteryData.status,
    startTime: lotteryData.startTime,
    endTime: lotteryData.endTime,
    priceTicketInMjt,
    discountDivisor,
    treasuryFee: lotteryData.treasuryFee,
    firstTicketId: lotteryData.firstTicketId,
    lastTicketId: lotteryData.lastTicketId,
    amountCollectedInMjt,
    finalNumber: lotteryData.finalNumber,
    mjtPerBracket: lotteryData.mjtPerBracket,
    countWinnersPerBracket: lotteryData.countWinnersPerBracket,
    rewardsBreakdown: lotteryData.rewardsBreakdown,
  }
}

/**
 * API HELPERS
 */

/**
 * Fetch lottery auto config
 * @returns
 */
export const getLotteryAutoConfig = async (account: string): Promise<any> => {
  const apiLink = `${API_LOTTERY}/tickets?account=${account}`

  const res = await fetch(apiLink)
  const response = await res.json()
  if (response.success) {
    return response.data
  }
  console.error('Failed to fetch lottery auto config', response?.msg)
  return response.msg
}

/**
 * Fetch lottery auto order history
 * @returns
 */
export const getLotteryAutoHistory = async (account: string, page: number, limit?: number): Promise<string> => {
  const apiLink = `${API_LOTTERY}/orders?account=${account}&page=${page}&limit=${limit ?? 6}`

  const res = await fetch(apiLink)
  const response = await res.json()
  if (response.success) {
    return response.data
  }
  console.error('Failed to fetch lottery auto history', response?.msg)
  return response.msg
}

/**
 * Post lottery auto setting
 * @returns
 */

interface postAutoData {
  account: string
  type: string
  maxNum: number | string
  tickets: any[]
  startLotteryId: number | string
  sign: string
  data: string
}

export const postLotteryAuto = async (data: postAutoData) => {
  const res = await fetch(`${API_LOTTERY}/set_tickets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  const response = await res.json()
  if (!response.success) {
    console.error('Failed to post lottery auto', response.msg)
  }
  return response
}

/**
 * Post close lottery auto setting
 * @returns
 */
export const postLotteryCloseAuto = async (data) => {
  const res = await fetch(`${API_LOTTERY}/ticket_button`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  const response = await res.json()
  if (!response.success) {
    console.error('Failed to post close lottery auto', response.msg)
  }
  return response
}

/**
 * Fetch lottery referral history
 * @returns
 */
export const getLotteryReferralHistory = async (account: string, page: number): Promise<any> => {
  const apiLink = `${API_REFERRAL}/v1/account/orders?address=${account}&page=${page}&limit=6`

  const res = await fetch(apiLink)
  const response = await res.json()
  if (response.success) {
    return response.data
  }
  console.error('Failed to fetch lottery referral', response?.msg)
  return response.msg
}
